import { Injectable, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { SafetyTest } from '../data-models/safety-test.model';
import { SafetyTestStatus } from '../data-models/safety-test-status.model';
import { TestQuestion } from '../response-models/test-question.model';
import { isRecord } from '../utils/is-record';
import { toTestQuestions } from '../utils/safety-test-utils';
import { nullableString } from '../utils/wire-value-utils';
import { openExternal } from '../utils/external-link-utils';

const DAY_MS = 1000 * 60 * 60 * 24;

/**
 * Safety tests with the signed-in user's status: `/safety-tests[/:id[/questions|/study-guide]]`
 * (hub `user-safety-tests-controller.ts`). Not paged. Attempts live in `TestAttemptService`, categories in
 * `SafetyTestCategoryService`.
 *
 * Holds the loaded tests as signal state. Failures are recorded in `error` (the hub's message).
 */
@Injectable({
  providedIn: 'root'
})
export class SafetyTestService extends BaseService<SafetyTest> {
  private testsSignal = signal<SafetyTest[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  readonly tests = computed(() => this.testsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  readonly completedTests = computed(() => this.testsSignal().filter(t => t.status === 'passed').length);
  readonly totalTests = computed(() => this.testsSignal().length);
  readonly expiredTests = computed(() => this.testsSignal().filter(t => t.status === 'expired').length);
  readonly expiringSoonTests = computed(() => this.testsSignal().filter(t => this.isExpiringSoon(t)));
  readonly requiredTests = computed(() => this.testsSignal().filter(t => t.requiredAnnually));

  constructor() {
    super('safety-tests', SafetyTest);
  }

  /** Load the organization's available tests into `tests`. */
  async loadTests(): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      this.testsSignal.set(await firstValueFrom(this.getMany()));
    } catch (error: unknown) {
      const apiError = ApiError.fromHttpError(error);
      console.error('[SafetyTestService] Failed to load tests:', apiError.message);
      this.errorSignal.set(apiError.errors[0] ?? 'Failed to load tests');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  getTests(): SafetyTest[] {
    return this.testsSignal();
  }

  getTestById(id: string): SafetyTest | undefined {
    return this.testsSignal().find(t => t.id === id);
  }

  /** Fetch one test with the user's status; resolves `null` on failure. */
  async fetchTestById(id: string): Promise<SafetyTest | null> {
    try {
      return await firstValueFrom(this.get(id));
    } catch (error: unknown) {
      console.error('Error fetching test:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /**
   * A test's questions for taking (no correct answers), shuffled and capped by the hub.
   * Rejects with an `ApiError` (e.g. when the test is not available).
   */
  fetchQuestions(testId: string): Promise<TestQuestion[]> {
    return firstValueFrom(this.request('GET', { id: testId, path: 'questions' }, toTestQuestions));
  }

  /** Tests grouped by category id ('uncategorized' when a test has none). */
  getTestsByCategory(): Map<string, SafetyTest[]> {
    const grouped = new Map<string, SafetyTest[]>();
    for (const test of this.testsSignal()) {
      const categoryId = test.categoryId || 'uncategorized';
      grouped.set(categoryId, [...(grouped.get(categoryId) || []), test]);
    }
    return grouped;
  }

  /** A signed study guide download URL (valid for an hour); resolves `null` on failure. */
  async getStudyGuideUrl(testId: string): Promise<string | null> {
    try {
      return await firstValueFrom(this.request('GET', { id: testId, path: 'study-guide' }, toDownloadUrl));
    } catch (error: unknown) {
      console.error('Error getting study guide URL:', ApiError.fromHttpError(error).message);
      return null;
    }
  }

  /** Open a test's study guide in a new tab. */
  async viewStudyGuide(testId: string): Promise<void> {
    const url = await this.getStudyGuideUrl(testId);
    if (url) {
      openExternal(url);
    }
  }

  /** Records a status change locally (after starting or submitting an attempt) without refetching. */
  markTestStatus(testId: string, status: SafetyTestStatus, score?: number): void {
    this.testsSignal.update(tests => tests.map(test => {
      if (test.id !== testId) return test;
      const copy = Object.assign(Object.create(Object.getPrototypeOf(test)) as SafetyTest, test);
      copy.status = status;
      copy.lastScore = score ?? test.lastScore;
      copy.lastAttemptDate = new Date().toISOString().split('T')[0];
      return copy;
    }));
  }

  getStatusText(status: SafetyTestStatus): string {
    switch (status) {
      case 'passed': return 'Passed';
      case 'failed': return 'Failed';
      case 'expired': return 'Expired';
      case 'in_progress': return 'In Progress';
      case 'new_version_available': return 'Update Available';
      case 'not_started':
      default: return 'Not Started';
    }
  }

  getStatusBadgeClass(status: SafetyTestStatus): string {
    switch (status) {
      case 'passed': return 'bg-success';
      case 'failed': return 'bg-danger';
      case 'expired': return 'bg-warning text-dark';
      case 'in_progress': return 'bg-info';
      case 'new_version_available': return 'bg-primary';
      case 'not_started':
      default: return 'bg-secondary';
    }
  }

  isExpiringSoon(test: SafetyTest): boolean {
    if (!test.expirationDate || test.status !== 'passed') return false;
    const days = this.getDaysUntilExpiration(test);
    return days > 0 && days <= 30;
  }

  getDaysUntilExpiration(test: SafetyTest): number {
    if (!test.expirationDate) return 0;
    return Math.ceil((new Date(test.expirationDate).getTime() - Date.now()) / DAY_MS);
  }
}

function toDownloadUrl(data: unknown): string | null {
  return (isRecord(data) ? nullableString(data['downloadUrl']) : undefined) ?? null;
}
